import { Alert, Card, Space } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { PropsWithChildren, useCallback, useState } from 'react'

import { ServerValidationError } from '@/api/common'
import { useTranslation } from '@/contexts/TranslationContext'
import { AxiosError } from 'axios'
import classes from './form.module.css'

type FormProps<T> = {
  form: UseFormReturnType<T>
  onSubmit: (values: T) => Promise<void>
  withStyle?: boolean
  showClientValidationError?: boolean
  bg?: string
  shadow?: string
  withBorder?: boolean
  overflow?:
    | 'visible'
    | 'hidden'
    | 'scroll'
    | 'auto'
    | 'initial'
    | 'inherit'
    | 'unset'
}

export function Form<T>({
  form,
  onSubmit,
  withStyle = true,
  showClientValidationError = true,
  children,
  bg,
  shadow = 'sm',
  withBorder = true,
  overflow = 'hidden',
}: PropsWithChildren<FormProps<T>>) {
  const { translateErrorMessage } = useTranslation()
  const [serverError, setServerError] = useState<string | undefined>(undefined)
  const handleSubmit = useCallback(
    async (values: T) => {
      form.setSubmitting(true)
      try {
        await onSubmit(values)
        setServerError(undefined)
      } catch (error) {
        if (error instanceof ServerValidationError) {
          const mantineErrors: Record<string, React.ReactNode> = {}
          error.fields.forEach((field) => {
            mantineErrors[field.name] = translateErrorMessage(field.message)
          })

          form.setErrors(mantineErrors)
        } else if (error instanceof AxiosError) {
          setServerError(
            `<li>${error.response?.data.message || 'An error occurred, but no message'}</li>`
          )
        } else if (error instanceof Error) {
          setServerError(
            `<li>${error.message || 'An error occurred, but no message'}</li>`
          )
        } else {
          setServerError('<li>An unknown error occurred</li>')
        }
      } finally {
        form.setSubmitting(false)
      }
    },
    [onSubmit, form, translateErrorMessage]
  )

  const formErrors = []
  if (Object.keys(form.errors).length > 0) {
    console.log('form.errors', form.errors)
    //  loop through form.errors and push to formErrors
    for (const key in form.errors) {
      if (Object.prototype.hasOwnProperty.call(form.errors, key)) {
        const errorMessage = form.errors[key] as string

        // Check if this is a table validation error (items.x.field pattern)
        const tableErrorMatch = key.match(/^items\.(\d+)\.(\w+)$/)
        if (tableErrorMatch) {
          const rowIndex = parseInt(tableErrorMatch[1]) + 1 // Convert to 1-based index
          const fieldName = tableErrorMatch[2]
          const friendlyFieldName = getFieldDisplayName(fieldName)
          formErrors.push(
            `รายการที่ ${rowIndex}: ${errorMessage.replace(fieldName, friendlyFieldName)}`
          )
        } else {
          formErrors.push(key + ': ' + errorMessage)
        }
      }
    }
  }

  // Helper function to get display name for field
  function getFieldDisplayName(fieldName: string): string {
    const fieldNameMap: Record<string, string> = {
      mold: 'Mold',
      itemId: 'สินค้า/วัตถุดิบ',
      lotNo: 'Lot No.',
      qty: 'จำนวน',
      unit: 'หน่วย',
      refNo: 'เลขที่อ้างอิง',
    }
    return fieldNameMap[fieldName] || fieldName
  }

  const alert = serverError ? (
    <>
      <Alert color="red" title="เกิดความผิดผลาดในการทำงาน">
        <ul dangerouslySetInnerHTML={{ __html: serverError }} />
      </Alert>
      <Space h="md" />
    </>
  ) : null
  return (
    <Card
      shadow={shadow}
      mx={{ base: 'none', md: 'sm' }}
      radius="md"
      mb="md"
      withBorder={withBorder}
      bg={bg}
      style={{ overflow }}
    >
      <form
        className={withStyle ? classes.form : ''}
        onSubmit={form.onSubmit(handleSubmit)}
        onKeyDown={preventEnterKeySubmission}
      >
        {alert}
        {showClientValidationError && formErrors.length > 0 && (
          <>
            <Alert color="red" title="กรุณาตรวจสอบการป้อนข้อมูล">
              <ul>
                {formErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
            <Space h="md" />
          </>
        )}
        {children}
      </form>
    </Card>
  )
}

const preventEnterKeySubmission = (e: React.KeyboardEvent<HTMLElement>) => {
  const target = e.target as HTMLElement
  if (e.key === 'Enter' && target.tagName === 'INPUT') {
    e.preventDefault()
  }
}
